import { I18n } from "@assets/strings";
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { Path, searchParams } from "@core/routing/path";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Category } from "@domain/lms/category/category";
import { Course } from "@domain/lms/course/course";

import { Carousel } from "@domain/carousel/carousel";
import { Article } from "@domain/lms/article/article";
import { Organization } from "@domain/organization/organization";
import { useLastSearches, useSearchSuggestions } from "@domain/search/hook";
import { SearchDomain } from "@domain/search/searchDomain";
import { Profile } from "@domain/user/profile";
import { desktop, mobile } from "@ui/styles/utils";
import { CategoriesView } from "@ui/components/categoriesView";
import { Primary, PrimaryBold } from "@ui/components/containers/primary";
import { HatIcon, KeyBoardIcon } from "@ui/components/icons";
import { row, Stack } from "@ui/components/layout";
import { FollowButton } from "@ui/components/profile/followButton";
import { UserView } from "@ui/components/profile/userView";

const ITEMS_BY_CATEGORY = 4;

interface AutocompletionViewProps extends React.HTMLAttributes<HTMLDivElement> {
	inputRef?: React.RefObject<HTMLInputElement | null>;
	inputValue: string;
	onSelected: (value: string) => void;
	onReset: () => void;
	hidden?: boolean;
}
export interface AutocompletionViewHandle {
	isHovered: boolean;
}

export const AutocompletionView = forwardRef<AutocompletionViewHandle, AutocompletionViewProps>(
	({ inputRef, inputValue, onSelected, onReset, hidden, ...htmlProps }, ref) => {
		const currentProfile = useCurrentProfile();

		const { result: courses, loading: coursesLoading } = useSearchSuggestions(inputValue, SearchDomain.Course);
		const { result: articles, loading: articlesLoading } = useSearchSuggestions(inputValue, SearchDomain.Article);
		const { result: persons, loading: personsLoading } = useSearchSuggestions(inputValue, SearchDomain.User);
		const { result: organizations, loading: orgLoading } = useSearchSuggestions(inputValue, SearchDomain.Organization);
		const { result: carousels, loading: carouselsLoading } = useSearchSuggestions(inputValue, SearchDomain.Carousel);

		const displayedCourses = courses.slice(0, ITEMS_BY_CATEGORY) as Course[];
		const displayedArticles = articles.slice(0, ITEMS_BY_CATEGORY) as Article[];
		const displayedCarousels = carousels.slice(0, ITEMS_BY_CATEGORY) as Carousel[];
		const displayedPersons = persons.slice(0, ITEMS_BY_CATEGORY) as Profile[];
		const displayedOrganizations = organizations.slice(0, ITEMS_BY_CATEGORY) as Organization[];

		const allResults = useMemo(
			() => [
				...displayedCourses,
				...displayedArticles,
				...displayedCarousels,
				...displayedPersons,
				...displayedOrganizations,
			],
			[courses, articles, carousels, persons, organizations]
		);

		const lastSearches = useLastSearches();

		const [hoverIndex, setHoverIndex] = useState(-1);
		const history = useHistory();

		useImperativeHandle(ref, () => ({
			isHovered: hoverIndex !== -1,
		}));

		const onKeyPress = (e: KeyboardEvent): void => {
			if (!hidden) {
				if (e.key === "Enter" && hoverIndex !== -1) {
					if (hoverIndex < displayedCourses.length) {
						history.push(Path.Lms.ReferenceCourse(displayedCourses[hoverIndex]));
					} else if (hoverIndex < displayedCourses.length + displayedPersons.length) {
						history.push(Path.Profile(displayedPersons[hoverIndex - displayedCourses.length]));
					} else {
						history.push(
							Path.Organization(displayedOrganizations[hoverIndex - displayedCourses.length - displayedPersons.length])
						);
					}
					onReset();
				}
			}
		};

		const onKeyDown = (e: KeyboardEvent): void => {
			if (!hidden) {
				if (e.key === "ArrowDown") {
					const index = hoverIndex === allResults.length - 1 ? 0 : hoverIndex + 1;
					setHoverIndex(index);
				} else if (e.key === "ArrowUp") {
					const index = hoverIndex === 0 || hoverIndex === -1 ? allResults.length - 1 : hoverIndex - 1;
					setHoverIndex(index);
				} else if (e.key !== "Enter") {
					setHoverIndex(-1);
				}
			}
		};

		useEffect(() => {
			if (inputRef?.current) {
				inputRef.current.onkeypress = onKeyPress;
				inputRef.current.onkeydown = onKeyDown;
			}
			return () => {
				if (inputRef?.current) {
					inputRef.current.onkeypress = null;
					inputRef.current.onkeydown = null;
				}
			};
		});

		useEffect(() => {
			setHoverIndex(-1);
		}, [hidden]);

		return !hidden ? (
			<div {...htmlProps}>
				{inputValue ? (
					<>
						{coursesLoading ? (
							<ClipLoader />
						) : (
							displayedCourses.map((course, i) => (
								<ResultItem
									key={course.id}
									hover={i === hoverIndex}
									onHover={(isHover) => setHoverIndex(isHover ? i : -1)}
									onClick={() => {
										history.push(Path.Lms.ReferenceCourse(displayedCourses[hoverIndex]));
										onReset();
									}}
								>
									<SearchResult>
										<BlackHatIcon />
										<div>{course.title}</div>
									</SearchResult>
								</ResultItem>
							))
						)}
						{((courses.length > 0 && persons.length > 0) || coursesLoading) && <Divider />}

						{articlesLoading ? (
							<ClipLoader />
						) : (
							displayedArticles.map((article, i) => (
								<ResultItem
									key={article.id}
									hover={i + displayedArticles.length === hoverIndex}
									onHover={(isHover) => setHoverIndex(isHover ? i + displayedArticles.length : -1)}
									onClick={() => {
										history.push(Path.Article(article.id));
										onReset();
									}}
								>
									<SearchResult>
										<BlackKeyboardIcon />
										<div>{article.title}</div>
									</SearchResult>
								</ResultItem>
							))
						)}
						{(((articles.length > 0 || courses.length > 0) && organizations.length > 0) || personsLoading) && (
							<Divider />
						)}

						{carouselsLoading ? (
							<ClipLoader />
						) : (
							displayedCarousels.map((carousel, i) => (
								<ResultItem
									key={carousel.id}
									hover={i + displayedCarousels.length === hoverIndex}
									onHover={(isHover) => setHoverIndex(isHover ? i + displayedCarousels.length : -1)}
									onClick={() => {
										history.push(Path.Carousel(carousel.id));
										onReset();
									}}
								>
									<SearchResult>
										<BlackKeyboardIcon />
										<div>{carousel.title}</div>
									</SearchResult>
								</ResultItem>
							))
						)}
						{(((carousels.length > 0 || courses.length > 0) && organizations.length > 0) || personsLoading) && (
							<Divider />
						)}

						{personsLoading ? (
							<ClipLoader />
						) : (
							displayedPersons.map((person, i) => (
								<ResultItem
									key={person.id}
									hover={i + displayedCourses.length === hoverIndex}
									onHover={(isHover) => setHoverIndex(isHover ? i + displayedCourses.length : -1)}
								>
									<Row>
										<UserView user={person} showDescription pictureSize={"S"} onClick={() => onReset()} />
										<FollowButton profile={person} />
									</Row>
								</ResultItem>
							))
						)}
						{(((articles.length > 0 || persons.length > 0 || courses.length > 0) && organizations.length > 0) ||
							personsLoading) && <Divider />}
						{orgLoading ? (
							<ClipLoader />
						) : (
							displayedOrganizations.map((org, i) => (
								<ResultItem
									key={org.id}
									hover={i + displayedCourses.length + displayedPersons.length === hoverIndex}
									onHover={(isHover) =>
										setHoverIndex(isHover ? i + displayedCourses.length + displayedPersons.length : -1)
									}
								>
									<Row>
										<UserView user={org} showDescription pictureSize={"S"} hidePicture onClick={() => onReset()} />
										<FollowButton isOrg profile={org} />
									</Row>
								</ResultItem>
							))
						)}
					</>
				) : (
					<EmptyContainer>
						<Stack gap={25}>
							{lastSearches.length > 0 && (
								<Stack gap={10}>
									<PrimaryBold>{I18n.t("search.last.title")}</PrimaryBold>
									<Primary>
										{lastSearches.map((q) => (
											<Suggestion key={q} onMouseDown={() => onSelected(q)}>
												{q}
											</Suggestion>
										))}
									</Primary>
								</Stack>
							)}
							<Stack gap={10}>
								<PrimaryBold>{I18n.t("profile.categories.title")}</PrimaryBold>
								{currentProfile?.categories && (
									<StyledCategoriesView
										showAll
										categories={currentProfile.categories}
										onItemClick={(cat) => {
											history.push({
												pathname: Path.Search,
												search: searchParams(inputValue, undefined, [cat as Category]),
											});
											onReset();
										}}
									/>
								)}
							</Stack>
						</Stack>
					</EmptyContainer>
				)}
			</div>
		) : null;
	}
);

const EmptyContainer = styled.div`
	padding: 15px;
	@media ${desktop} {
		margin-top: 10px;
	}
`;

const StyledCategoriesView = styled(CategoriesView)`
	${row(6, "flex-start", "center")};
	margin-bottom: 10px;
`;

const Divider = styled.div`
	height: 1px;
	background-color: ${({ theme }) => theme.colors.lightgray};
	width: 100%;
	margin: 10px 0px;
`;

const SearchResult = styled.div`
	${({ theme }) => theme.textStyles.text};
	${row(10, "flex-start", "center")};
	> div {
		white-space: nowrap;
		text-overflow: ellipsis;
		max-width: 100%;
		overflow: hidden;
	}

	> svg {
		min-width: 15px;
		max-width: 15px;
		height: 15px;
	}
`;

const Row = styled.div`
	${row(0, "space-between", "center")};
	> button {
		flex-shrink: 0;
	}
`;

const BlackHatIcon = styled(HatIcon)`
	path {
		fill: ${({ theme }) => theme.colors.black};
	}
`;

const BlackKeyboardIcon = styled(KeyBoardIcon)`
	path {
		fill: ${({ theme }) => theme.colors.black};
	}
`;

interface ResultItemProps extends React.HTMLAttributes<HTMLDivElement> {
	hover: boolean;
	onHover: (hover: boolean) => void;
	onClick?: () => void;
}

export const ResultItem: React.FC<ResultItemProps> = ({ hover, onHover, onClick, children, ...htmlProps }) => {
	return (
		<ResultContainer
			{...htmlProps}
			hover={hover}
			onMouseEnter={() => onHover(true)}
			onMouseLeave={() => onHover(false)}
			onMouseDown={(e) => {
				e.nativeEvent.stopImmediatePropagation();
				e.preventDefault();
				onClick?.();
			}}
		>
			{children}
		</ResultContainer>
	);
};

const ResultContainer = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "hover",
})<{ hover: boolean }>`
	cursor: pointer;
	max-width: 100%;
	padding: 8px 15px;
	background-color: ${({ theme, hover }) => (hover ? theme.colors.secondaryLight : theme.colors.white)};
	color: ${({ theme, hover }) => (hover ? theme.colors.secondary : theme.colors.textPrimary)};
	overflow: hidden;
	@media ${mobile} {
		background-color: transparent;
	}
`;

const Suggestion = styled.div`
	cursor: pointer;
	font-style: italic;
	margin-bottom: 4px;

	&:hover {
		color: ${({ theme }) => theme.colors.secondary};
	}
`;
